import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { apiFetch } from "../api/api";
import {
    Activity, Radar, Server, Wifi, WifiOff, Clock, X
} from "lucide-react";

// Color palette for categories
const CATEGORY_COLORS = [
    "#3b82f6", "#ef4444", "#f59e0b", "#10b981", "#8b5cf6",
    "#ec4899", "#06b6d4", "#f97316", "#6366f1", "#14b8a6"
];

const COLOR_PALETTE = {
    critical: "#ff3e3e",
    high: "#f59e0b",
    warning: "#ffd12b",
    notice: "#00d4ff",
    info: "#00d4ff",
    secure: "#00ff9d"
};

export default function GlobalThreatMap() {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const arcsLayer = useRef(null);
    const [feed, setFeed] = useState([]);
    const [activeTab, setActiveTab] = useState(null);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [stats, setStats] = useState({
        scanned: 0,
        detected: 0,
        blocked: 0
    });
    const [distribution, setDistribution] = useState(null);

    // Initialize Map
    useEffect(() => {
        if (!mapRef.current || mapInstance.current) return;

        const map = L.map(mapRef.current, {
            center: [30, 0],
            zoom: 2.5,
            zoomControl: false,
            attributionControl: false,
            maxBounds: [[-85, -180], [85, 180]],
            minZoom: 2,
            scrollWheelZoom: true,
            dragging: true,
        });

        // Use ESRI ArcGIS Dark Gray Canvas - free, high performance, no watermark, no API key required
        L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
            maxZoom: 16,
            minZoom: 2,
            opacity: 1,
            zIndex: 1,
            className: 'esri-dark-tiles'
        }).addTo(map);

        // Labels and reference layer
        L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}', {
            maxZoom: 16,
            minZoom: 2,
            opacity: 0.85,
            zIndex: 650,
            pane: 'overlayPane'
        }).addTo(map);

        // Attack layers
        arcsLayer.current = L.layerGroup().addTo(map);

        mapInstance.current = map;

        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
        };
    }, []);

    // Create a quadratic bezier curve for the arc
    const createArcPoints = (start, end, pointsCount = 30) => {
        const p1 = [start[0], start[1]];
        const p3 = [end[0], end[1]];

        const midLat = (p1[0] + p3[0]) / 2;
        const midLng = (p1[1] + p3[1]) / 2;
        const dist = Math.sqrt(Math.pow(p3[0] - p1[0], 2) + Math.pow(p3[1] - p1[1], 2));
        const offset = Math.max(dist * 0.4, 15);
        const p2 = [midLat + offset, midLng];

        const points = [];
        for (let i = 0; i <= pointsCount; i++) {
            const t = i / pointsCount;
            const lat = (1 - t) * (1 - t) * p1[0] + 2 * (1 - t) * t * p2[0] + t * t * p3[0];
            const lng = (1 - t) * (1 - t) * p1[1] + 2 * (1 - t) * t * p2[1] + t * t * p3[1];
            points.push([lat, lng]);
        }
        return points;
    };

    const drawBallisticAttack = useCallback((start, end, severity) => {
        if (!mapInstance.current || !arcsLayer.current) return;

        const color = COLOR_PALETTE[severity?.toLowerCase()] || COLOR_PALETTE.notice;
        const points = createArcPoints(start, end);

        // Static path trail
        const staticPath = L.polyline(points, {
            color: color,
            weight: 1,
            opacity: 0.15,
            smoothFactor: 2
        }).addTo(arcsLayer.current);

        // Rocket/comet line
        const rocketLine = L.polyline(points, {
            color: color,
            weight: 2,
            opacity: 0.8,
            className: 'rocket-attack-path',
            smoothFactor: 2
        }).addTo(arcsLayer.current);

        // Impact bloom
        setTimeout(() => {
            const impactIcon = L.divIcon({
                className: 'attack-impact-container',
                html: `<div class="impact-bloom" style="--impact-color: ${color}"></div>`,
                iconSize: [40, 40],
                iconAnchor: [20, 20]
            });
            const impact = L.marker(end, { icon: impactIcon }).addTo(arcsLayer.current);

            setTimeout(() => {
                if (arcsLayer.current) {
                    arcsLayer.current.removeLayer(staticPath);
                    arcsLayer.current.removeLayer(rocketLine);
                    arcsLayer.current.removeLayer(impact);
                }
            }, 3000);
        }, 1800);
    }, []);

    const loadData = useCallback(async () => {
        try {
            const { ok, data } = await apiFetch("/api/soc/attack-map?limit=25");
            if (ok && data.logs) {
                const validLogs = data.logs;
                setFeed(prev => [...validLogs, ...prev].slice(0, 50));

                if (validLogs.length > 0) {
                    const criticalAttack = validLogs.find(l => l.severity === 'critical');
                    if (criticalAttack && mapInstance.current) {
                        mapInstance.current.flyTo([criticalAttack.dstLat, criticalAttack.dstLng], 4, {
                            animate: true,
                            duration: 3
                        });
                    }

                    validLogs.forEach((l, idx) => {
                        setTimeout(() => {
                            if (l.srcLat && l.srcLng && l.dstLat && l.dstLng) {
                                drawBallisticAttack([l.srcLat, l.srcLng], [l.dstLat, l.dstLng], l.severity);
                            }
                        }, idx * 450);
                    });
                }

                if (data.stats) {
                    setStats(data.stats);
                } else {
                    setStats(prev => ({
                        scanned: prev.scanned + validLogs.length,
                        detected: prev.detected + validLogs.filter(l => l.severity === 'critical' || l.severity === 'high').length,
                        blocked: prev.blocked + validLogs.filter(l => l.action === 'deny' || l.action === 'drop' || l.action === 'block').length
                    }));
                }
            }
        } catch (err) {
            console.error("SOC Map Fetch Failed", err);
        }
    }, [drawBallisticAttack]);

    const loadDistribution = useCallback(async () => {
        try {
            const { ok, data } = await apiFetch("/api/soc/attack-map/distribution");
            if (ok && data) {
                setDistribution(data);
            }
        } catch (err) {
            console.error("Distribution fetch failed", err);
        }
    }, []);

    const feedRef = useRef(feed);
    feedRef.current = feed;

    // Camera Loop
    useEffect(() => {
        const cycle = async () => {
            if (!mapInstance.current || feedRef.current.length === 0) return;

            const randomAttack = feedRef.current[Math.floor(Math.random() * feedRef.current.length)];
            if (randomAttack && randomAttack.dstLat) {
                mapInstance.current.flyTo([randomAttack.dstLat, randomAttack.dstLng], 5, {
                    animate: true,
                    duration: 3
                });
                await new Promise(r => setTimeout(r, 6000));
                if (mapInstance.current) {
                    mapInstance.current.flyTo([30, 0], 2.5, {
                        animate: true,
                        duration: 3
                    });
                }
            }
        };

        const interval = setInterval(cycle, 15000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        loadData();
        loadDistribution();
        const dataInterval = setInterval(loadData, 7000);
        const distInterval = setInterval(loadDistribution, 120000);
        return () => {
            clearInterval(dataInterval);
            clearInterval(distInterval);
        };
    }, [loadData, loadDistribution]);

    // Compute real distribution for the donut chart
    const topCategories = distribution?.categories?.slice(0, 3) || [];
    const blockedPct = stats.scanned > 0 ? ((stats.blocked / stats.scanned) * 100).toFixed(1) : '0.0';
    const detectedPct = stats.scanned > 0 ? ((stats.detected / stats.scanned) * 100).toFixed(2) : '0.00';
    const securityScore = stats.scanned > 0 ? (100 - parseFloat(detectedPct)).toFixed(2) : '100.00';
    const nodes = distribution?.nodes || [];

    // SVG donut values for real category distribution
    const donutSegments = topCategories.map((cat, idx) => ({
        ...cat,
        color: CATEGORY_COLORS[idx] || '#64748b'
    }));

    // Calculate dash values for SVG circle donut
    const circumference = 2 * Math.PI * 16; // r=16

    return (
        <div className="relative w-full h-screen bg-[#111827] overflow-hidden font-sans">
            {/* VECTOR MAP AREA */}
            <div ref={mapRef} className="absolute inset-0 z-0 bg-[#050810]" />

            {/* GRID & SCANNER OVERLAYS */}
            <div className="absolute inset-0 z-[1] pointer-events-none bg-[radial-gradient(circle_at_center,transparent_0%,rgba(2,6,23,0.4)_100%)]" />
            <div className="absolute inset-0 z-[1] pointer-events-none opacity-[0.02]"
                style={{ backgroundImage: 'linear-gradient(#4f46e5 1px, transparent 1px), linear-gradient(90deg, #4f46e5 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

            <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent shadow-[0_0_30px_rgba(59,130,246,0.5)] animate-scanning-matrix z-[2]" />

            {/* COMMAND SIDE PANEL: LEFT BOTTOM */}
            <div className="absolute bottom-40 left-8 z-10 space-y-4 pointer-events-none">
                <div className="bg-[#111827]/95 backdrop-blur-2xl p-5 rounded-3xl border border-white/[0.06] shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-1000 shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-600/20 rounded-xl flex items-center justify-center border border-blue-500/30">
                            <Radar className="w-6 h-6 text-blue-400 animate-pulse" />
                        </div>
                        <div>
                            <h1 className="text-lg font-black text-slate-100 tracking-widest uppercase italic leading-tight">Live Threat Map</h1>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
                                <p className="text-[8px] text-emerald-400 font-bold uppercase tracking-[0.2em]">Operational</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-6">
                        <div
                            className="space-y-0.5 cursor-pointer hover:bg-[#1a2332] p-2 rounded-xl transition-colors pointer-events-auto"
                            onClick={() => setActiveTab('nodes')}
                        >
                            <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Total Logs</p>
                            <p className="text-xl font-black text-slate-100 mono tracking-tighter">{stats.scanned.toLocaleString()}</p>
                        </div>
                        <div
                            className="space-y-0.5 cursor-pointer hover:bg-[#1a2332] p-2 rounded-xl transition-colors pointer-events-auto"
                            onClick={() => setActiveTab('blocked')}
                        >
                            <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Blocked / Denied</p>
                            <p className="text-xl font-black text-red-500 mono tracking-tighter">{stats.blocked.toLocaleString()}</p>
                        </div>
                    </div>
                </div>

                {/* DISTRIBUTION - REAL DATA */}
                <div className="bg-[#111827]/95 backdrop-blur-xl p-4 rounded-3xl border border-white/[0.06] w-72 space-y-3 pointer-events-auto shadow-2xl">
                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                        <Activity className="w-3 h-3 text-blue-500" /> Traffic Distribution
                    </p>
                    <div className="flex items-center gap-3">
                        <div className="relative w-12 h-12 cursor-pointer hover:scale-105 transition-transform shrink-0" onClick={() => setActiveTab('distribution')}>
                            <svg className="w-full h-full" viewBox="0 0 36 36">
                                <circle cx="18" cy="18" r="16" fill="none" stroke="#1e293b" strokeWidth="4"></circle>
                                {(() => {
                                    let offset = 25;
                                    return donutSegments.map((seg, idx) => {
                                        const dashLen = (seg.percentage / 100) * circumference;
                                        const dashGap = circumference - dashLen;
                                        const currentOffset = offset;
                                        offset += dashLen;
                                        return (
                                            <circle key={idx} cx="18" cy="18" r="16" fill="none"
                                                stroke={seg.color}
                                                strokeWidth="4"
                                                strokeDasharray={`${dashLen}, ${dashGap}`}
                                                strokeDashoffset={-currentOffset}
                                            />
                                        );
                                    });
                                })()}
                            </svg>
                        </div>
                        <div className="flex-1 space-y-1.5">
                            {topCategories.map((cat, i) => (
                                <div
                                    key={i}
                                    className="flex justify-between items-center text-[7px] font-bold text-slate-400 uppercase cursor-pointer hover:text-blue-400 transition-colors"
                                    onClick={() => {
                                        setActiveTab('distribution');
                                        setSelectedDetail(cat.label);
                                    }}
                                >
                                    <div className="flex items-center gap-1">
                                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: CATEGORY_COLORS[i] }}></div>
                                        <span className="truncate max-w-[80px]">{cat.label}</span>
                                    </div>
                                    <span>{cat.percentage}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* LIVE DATA STREAM: RIGHT */}
            <div className="absolute top-8 right-8 bottom-36 w-80 z-10 pointer-events-none">
                <div className="h-full bg-[#111827]/95 backdrop-blur-xl rounded-[2.5rem] border border-white/[0.06] pointer-events-auto shadow-2xl flex flex-col overflow-hidden max-h-[calc(100vh-12rem)]">
                    <div className="p-5 bg-[#1a2332] border-b border-white/[0.06] flex items-center justify-between shrink-0">
                        <h2 className="text-[10px] font-black text-slate-100 uppercase tracking-[0.2em] italic">Intelligence Feed</h2>
                        <span className="text-[7px] px-1.5 py-0.5 bg-red-500/10 text-red-500 border border-red-500/20 rounded font-black">LIVE</span>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-2.5 custom-scrollbar">
                        {feed.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-center p-4">
                                <Radar className="w-8 h-8 text-blue-400/40 animate-spin mb-3" />
                                <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">Awaiting telemetry stream...</p>
                            </div>
                        ) : (
                            feed.map((l, i) => (
                                <div key={i} className="bg-[#1a2332] p-3.5 rounded-2xl border border-white/[0.06] hover:border-blue-500/20 transition-all group shrink-0">
                                    <div className="flex justify-between items-start mb-1.5">
                                        <span className={`text-[7px] font-black uppercase px-1.5 py-0.5 rounded-md ${
                                            l.severity === 'critical' ? 'text-red-400 bg-red-400/10' :
                                            l.severity === 'high' || l.severity === 'warning' ? 'text-amber-400 bg-amber-400/10' :
                                            'text-blue-400 bg-blue-400/10'
                                        }`}>
                                            {l.severity}
                                        </span>
                                        <span className="text-[7px] font-mono text-slate-500 font-bold">
                                            {l.time || new Date(l.ts).toLocaleTimeString()}
                                        </span>
                                    </div>
                                    <div className="space-y-0.5">
                                        <p className="text-[9px] font-black text-slate-100 group-hover:text-blue-400 transition-colors uppercase truncate">
                                            {l.deviceName || l.devname || 'FortiGate'}
                                        </p>
                                        <p className="text-[8px] text-slate-500 font-mono tracking-tighter truncate">
                                            {l.sourceIp || l.srcip} → {l.destIp || l.dstip}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1">
                                            {l.category && (
                                                <span className="text-[6px] font-bold px-1 py-0.5 bg-blue-500/10 text-blue-400 rounded uppercase">
                                                    {l.category}
                                                </span>
                                            )}
                                            {l.action && (
                                                <span className={`text-[6px] font-bold px-1 py-0.5 rounded uppercase ${
                                                    ['deny', 'drop', 'block', 'reject'].includes(l.action)
                                                        ? 'bg-red-500/10 text-red-400'
                                                        : 'bg-emerald-500/10 text-emerald-400'
                                                }`}>
                                                    {l.action}
                                                </span>
                                            )}
                                            {(l.srcCountry || l.srccountry) && (
                                                <span className="text-[6px] font-mono text-slate-600">
                                                    {l.srccountry || l.srcCountry} → {l.dstcountry || l.destCountry || '—'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* TACTICAL AWARENESS LINE: CENTER BOTTOM - REAL DATA */}
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-[20] flex flex-col items-center pointer-events-none w-max">
                <div className="h-[3px] w-[500px] bg-gradient-to-r from-transparent via-blue-500 to-transparent shadow-[0_0_30px_rgba(59,130,246,0.6)] animate-pulse" />
                <div className="mt-5 px-12 py-4 bg-[#111827]/95 backdrop-blur-xl rounded-full border border-white/[0.06] flex items-center gap-12 shadow-[0_0_60px_rgba(0,0,0,0.8)] pointer-events-auto ring-1 ring-white/[0.1]">
                    <div className="flex flex-col items-center">
                        <p className="text-[9px] font-black text-blue-400 uppercase tracking-[0.3em] mb-0.5 italic">Security Score</p>
                        <div className="flex items-center gap-3">
                            <span className="text-2xl font-black text-slate-100 italic tracking-tighter">{securityScore}%</span>
                            <span className={`text-[8px] px-2 py-0.5 border rounded font-black tracking-widest ${
                                parseFloat(securityScore) > 95
                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                    : parseFloat(securityScore) > 80
                                    ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                    : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            }`}>
                                {parseFloat(securityScore) > 95 ? 'SECURE' : parseFloat(securityScore) > 80 ? 'MODERATE' : 'ELEVATED'}
                            </span>
                        </div>
                    </div>
                    <div className="w-px h-8 bg-[#1e293b]"></div>
                    <div className="flex flex-col items-center">
                        <p className="text-[9px] font-black text-slate-500 uppercase tracking-[0.3em] mb-0.5 italic">Block Rate</p>
                        <div className="flex items-center gap-3">
                            <span className="text-2xl font-black text-slate-100 italic tracking-tighter">{blockedPct}%</span>
                            <span className="text-[8px] px-2 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded font-black tracking-widest">ENFORCED</span>
                        </div>
                    </div>
                    <div className="w-px h-8 bg-[#1e293b]"></div>
                    <div className="flex flex-col items-center">
                        <p className="text-[9px] font-black text-slate-500 uppercase tracking-[0.3em] mb-0.5 italic">Active Gateways</p>
                        <div className="flex items-center gap-3">
                            <span className="text-2xl font-black text-slate-100 italic tracking-tighter">{nodes.length}</span>
                            <span className="text-[8px] px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded font-black tracking-widest">ONLINE</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* DETAIL MODAL OVERLAY */}
            {activeTab && (
                <div className="absolute inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-8 animate-in fade-in duration-300">
                    <div className="bg-[#111827] border border-white/[0.06] w-full max-w-4xl h-[70vh] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-white/[0.06] flex justify-between items-center shrink-0">
                            <div>
                                <h3 className="text-2xl font-black text-slate-100 uppercase italic tracking-widest">
                                    {activeTab === 'blocked' && 'Blocked Traffic Log'}
                                    {activeTab === 'nodes' && 'Active FortiGate Gateways'}
                                    {activeTab === 'distribution' && 'Traffic Category Distribution'}
                                </h3>
                                <p className="text-blue-400 text-xs font-bold uppercase tracking-widest mt-1">Real-time MongoDB Telemetry</p>
                            </div>
                            <button
                                onClick={() => {
                                    setActiveTab(null);
                                    setSelectedDetail(null);
                                }}
                                className="w-12 h-12 rounded-2xl bg-[#1a2332] border border-white/[0.06] flex items-center justify-center text-slate-100 hover:bg-red-500/10 hover:border-red-500/20 transition-all cursor-pointer"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                            {/* BLOCKED ATTACKS LOG - REAL DATA ONLY */}
                            {activeTab === 'blocked' && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-xl">
                                            <span className="text-xs text-red-400 font-black font-mono">{stats.blocked.toLocaleString()}</span>
                                            <span className="text-[9px] text-slate-500 ml-2 uppercase">Total Blocked</span>
                                        </div>
                                        <div className="px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                                            <span className="text-xs text-blue-400 font-black font-mono">{stats.scanned.toLocaleString()}</span>
                                            <span className="text-[9px] text-slate-500 ml-2 uppercase">Total Processed</span>
                                        </div>
                                    </div>
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-black border-b border-white/[0.06]">
                                                <th className="pb-4">Timestamp</th>
                                                <th className="pb-4">Severity</th>
                                                <th className="pb-4">Source IP</th>
                                                <th className="pb-4">Destination</th>
                                                <th className="pb-4">Country</th>
                                                <th className="pb-4">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-xs font-mono">
                                            {feed.filter(l => ['deny', 'drop', 'block', 'close', 'reject'].includes(l.action)).length > 0 ? (
                                                feed.filter(l => ['deny', 'drop', 'block', 'close', 'reject'].includes(l.action)).map((l, i) => (
                                                    <tr key={i} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                                                        <td className="py-4 text-slate-500">{l.time || new Date(l.ts).toLocaleTimeString()}</td>
                                                        <td className="py-4">
                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-black ${
                                                                l.severity === 'critical' ? 'bg-red-500/10 text-red-500' :
                                                                l.severity === 'warning' || l.severity === 'high' ? 'bg-amber-500/10 text-amber-500' :
                                                                'bg-blue-500/10 text-blue-500'
                                                            }`}>
                                                                {(l.severity || 'notice').toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 text-slate-100 font-bold">{l.sourceIp || l.srcip}</td>
                                                        <td className="py-4 text-slate-400">{l.destIp || l.dstip}</td>
                                                        <td className="py-4 text-slate-500">{l.srccountry || l.srcCountry || '—'}</td>
                                                        <td className="py-4 text-red-400 font-black uppercase">{l.action}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                // Show all feed data when no specifically blocked traffic in the current batch
                                                feed.map((l, i) => (
                                                    <tr key={i} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                                                        <td className="py-4 text-slate-500">{l.time || new Date(l.ts).toLocaleTimeString()}</td>
                                                        <td className="py-4">
                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-black ${
                                                                l.severity === 'critical' ? 'bg-red-500/10 text-red-500' :
                                                                l.severity === 'warning' || l.severity === 'high' ? 'bg-amber-500/10 text-amber-500' :
                                                                'bg-blue-500/10 text-blue-500'
                                                            }`}>
                                                                {(l.severity || 'notice').toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 text-slate-100 font-bold">{l.sourceIp || l.srcip}</td>
                                                        <td className="py-4 text-slate-400">{l.destIp || l.dstip}</td>
                                                        <td className="py-4 text-slate-500">{l.srccountry || l.srcCountry || '—'}</td>
                                                        <td className={`py-4 font-black uppercase ${
                                                            ['deny', 'drop', 'block', 'reject'].includes(l.action) ? 'text-red-400' : 'text-emerald-400'
                                                        }`}>{l.action || 'processed'}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* ACTIVE NODES - REAL DATA */}
                            {activeTab === 'nodes' && (
                                <div className="space-y-6">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                                            <span className="text-xs text-blue-400 font-black font-mono">{stats.scanned.toLocaleString()}</span>
                                            <span className="text-[9px] text-slate-500 ml-2 uppercase">Total Logs Ingested</span>
                                        </div>
                                        <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                                            <span className="text-xs text-emerald-400 font-black font-mono">{nodes.length}</span>
                                            <span className="text-[9px] text-slate-500 ml-2 uppercase">Active Gateways</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-6">
                                        {nodes.map((node, i) => (
                                            <div key={i} className="bg-[#1a2332] p-6 rounded-3xl border border-white/[0.06] hover:border-blue-500/20 transition-all">
                                                <div className="flex justify-between items-center mb-4">
                                                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                                                        <Server className="w-5 h-5 text-blue-400" />
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        {node.status === 'online' ? (
                                                            <Wifi className="w-3 h-3 text-emerald-500" />
                                                        ) : (
                                                            <WifiOff className="w-3 h-3 text-amber-500" />
                                                        )}
                                                        <span className={`text-[10px] font-black uppercase ${
                                                            node.status === 'online' ? 'text-emerald-500' :
                                                            node.status === 'warning' ? 'text-amber-500' :
                                                            'text-red-500'
                                                        }`}>{node.status}</span>
                                                    </div>
                                                </div>
                                                <p className="text-slate-100 font-black text-sm mb-1 tracking-tight">{node.name}</p>
                                                <div className="flex items-center justify-between mt-2">
                                                    <p className="text-slate-500 text-[10px] font-mono flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />{node.lastSync}
                                                    </p>
                                                    <p className="text-blue-400 text-[10px] font-mono font-bold">{node.eventsProcessed?.toLocaleString()} events</p>
                                                </div>
                                            </div>
                                        ))}
                                        {nodes.length === 0 && (
                                            <div className="col-span-3 py-12 text-center">
                                                <Radar className="w-8 h-8 text-blue-400/30 animate-spin mx-auto mb-3" />
                                                <p className="text-slate-500 text-xs font-bold uppercase">Loading gateway data...</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* DISTRIBUTION - REAL DATA */}
                            {activeTab === 'distribution' && (
                                <div className="flex flex-col h-full">
                                    {/* Filter tabs based on real categories */}
                                    <div className="flex gap-3 mb-8 flex-wrap">
                                        <button
                                            onClick={() => setSelectedDetail(null)}
                                            className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border ${
                                                !selectedDetail
                                                    ? 'bg-blue-600 border-blue-500 text-slate-100 shadow-[0_0_15px_rgba(37,99,235,0.4)]'
                                                    : 'bg-[#1a2332] border-white/[0.06] text-slate-500 hover:text-blue-400'
                                            } cursor-pointer`}
                                        >
                                            All
                                        </button>
                                        {(distribution?.categories || []).slice(0, 6).map(cat => (
                                            <button
                                                key={cat.label}
                                                onClick={() => setSelectedDetail(selectedDetail === cat.label ? null : cat.label)}
                                                className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border cursor-pointer ${
                                                    selectedDetail === cat.label
                                                        ? 'bg-blue-600 border-blue-500 text-slate-100 shadow-[0_0_15px_rgba(37,99,235,0.4)]'
                                                        : 'bg-[#1a2332] border-white/[0.06] text-slate-500 hover:text-blue-400'
                                                }`}
                                            >
                                                {cat.label}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="grid grid-cols-2 gap-12 items-start flex-1">
                                        {/* Donut chart */}
                                        <div className="flex justify-center">
                                            <div className="relative w-64 h-64">
                                                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                                                    <circle cx="18" cy="18" r="16" fill="none" stroke="#1e293b" strokeWidth="3"></circle>
                                                    {(() => {
                                                        let offset = 0;
                                                        const cats = (distribution?.categories || []);
                                                        const filteredCats = selectedDetail ? cats.filter(c => c.label === selectedDetail) : cats;
                                                        return filteredCats.map((cat, idx) => {
                                                            const dash = (cat.percentage / 100) * circumference;
                                                            const gap = circumference - dash;
                                                            const currentOffset = offset;
                                                            offset += dash;
                                                            const colorIdx = cats.findIndex(c => c.label === cat.label);
                                                            return (
                                                                <circle key={idx} cx="18" cy="18" r="16" fill="none"
                                                                    stroke={CATEGORY_COLORS[colorIdx] || '#64748b'}
                                                                    strokeWidth="3"
                                                                    strokeDasharray={`${dash}, ${gap}`}
                                                                    strokeDashoffset={-currentOffset}
                                                                    className="drop-shadow-[0_0_8px_currentColor] transition-all duration-500"
                                                                />
                                                            );
                                                        });
                                                    })()}
                                                </svg>
                                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                    <span className="text-4xl font-black text-slate-100 italic">
                                                        {selectedDetail
                                                            ? `${distribution?.categories?.find(c => c.label === selectedDetail)?.percentage || 0}%`
                                                            : `${distribution?.totalLogs?.toLocaleString() || '0'}`
                                                        }
                                                    </span>
                                                    <span className="text-[8px] text-slate-500 font-black tracking-widest uppercase mt-1">
                                                        {selectedDetail ? selectedDetail : 'Total Logs'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Category detail cards */}
                                        <div className="space-y-4 max-h-[50vh] overflow-y-auto custom-scrollbar">
                                            {(distribution?.categories || [])
                                                .filter(cat => !selectedDetail || cat.label === selectedDetail)
                                                .map((cat, i) => {
                                                    const colorIdx = (distribution?.categories || []).findIndex(c => c.label === cat.label);
                                                    return (
                                                        <div key={i} className="bg-[#1a2332] p-5 rounded-2xl border border-white/[0.06] animate-in slide-in-from-right duration-500">
                                                            <div className="flex justify-between items-center mb-3">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-3 h-3 rounded-full shadow-[0_0_10px_currentColor]"
                                                                        style={{ background: CATEGORY_COLORS[colorIdx] || '#64748b' }}
                                                                    ></div>
                                                                    <span className="text-sm font-black text-slate-100 uppercase tracking-wider">{cat.label}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <span className="text-lg font-mono font-black text-slate-100">{cat.percentage}%</span>
                                                                    <span className="text-[9px] text-slate-500 font-mono">{cat.count.toLocaleString()} logs</span>
                                                                </div>
                                                            </div>
                                                            <div className="w-full h-2 bg-white/[0.06] rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full rounded-full transition-all duration-700"
                                                                    style={{
                                                                        width: `${Math.max(cat.percentage, 2)}%`,
                                                                        background: CATEGORY_COLORS[colorIdx] || '#64748b'
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div
                        className="absolute inset-0 -z-10 cursor-pointer"
                        onClick={() => {
                            setActiveTab(null);
                            setSelectedDetail(null);
                        }}
                    ></div>
                </div>
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                .animate-scanning-matrix { animation: scan-v 12s linear infinite; }
                @keyframes scan-v { 0% { top: 0; opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { top: 100%; opacity: 0; } }

                .animate-in { animation-duration: 500ms; animation-fill-mode: both; }
                .fade-in { animation-name: fade; }
                .zoom-in-95 { animation-name: zoom; }
                @keyframes fade { from { opacity: 0; } to { opacity: 1; } }
                @keyframes zoom { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }

                .rocket-attack-path {
                    stroke-dasharray: 50, 5000;
                    stroke-dashoffset: 5050;
                    animation: rocket-fly 2.2s ease-in-out forwards;
                    filter: drop-shadow(0 0 12px currentColor);
                }
                @keyframes rocket-fly {
                    0% { stroke-dashoffset: 5050; opacity: 0; }
                    5% { opacity: 1; }
                    95% { opacity: 1; }
                    100% { stroke-dashoffset: 0; opacity: 0; }
                }

                .impact-bloom {
                    width: 8px;
                    height: 8px;
                    background: var(--impact-color);
                    border-radius: 50%;
                    box-shadow: 0 0 20px var(--impact-color);
                    position: relative;
                }
                .impact-bloom::after {
                    content: '';
                    position: absolute;
                    inset: -20px;
                    border: 2px solid var(--impact-color);
                    border-radius: 50%;
                    animation: bloom-out 1.2s ease-out infinite;
                }
                @keyframes bloom-out {
                    0% { transform: scale(0.2); opacity: 1; }
                    100% { transform: scale(3); opacity: 0; }
                }

                .custom-scrollbar::-webkit-scrollbar { width: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 20px; }
                .mono { font-family: 'JetBrains Mono', 'Fira Code', monospace; }
                
                /* Leaflet Container Styling */
                .leaflet-container { 
                    background: #050810 !important; 
                }
                
                /* Ensure labels are visible */
                .leaflet-tile-pane { z-index: 200 !important; }
                .leaflet-overlay-pane { z-index: 400 !important; }
                .leaflet-shadow-pane { z-index: 500 !important; }
                .leaflet-marker-pane { z-index: 600 !important; }
                .leaflet-tooltip-pane { z-index: 650 !important; }
                .leaflet-popup-pane { z-index: 700 !important; }
            ` }} />
        </div>
    );
}
